<template>
  <div>
    <!-- Hero Section -->
    <Section class="pt-20 pb-16">
      <div class="text-center max-w-4xl mx-auto">
        <h1 class="text-4xl md:text-5xl font-bold text-ink mb-6">
          Schedule Your E2M Demo
        </h1>
        <p class="text-xl text-muted mb-4">
          See how E2M can transform your multi-channel retail operations
        </p>
        <p class="text-lg text-muted max-w-2xl mx-auto">
          Get a personalized walkthrough of how E2M helps brands centralize, optimize, and scale their online business across all major marketplaces. Learn how to save time, reduce errors, and maximize revenue.
        </p>
      </div>
    </Section>

    <!-- Demo Form Section -->
    <Section background="gray" class="py-20">
      <div class="max-w-2xl mx-auto">
        <h2 class="text-3xl font-bold text-ink text-center mb-8">
          Request Your Demo
        </h2>
        <p class="text-center text-muted mb-8">
          Fill out the form below, and our team will contact you within 24 hours to schedule your personalized demo.
        </p>
        
        <BaseCard>
          <form @submit.prevent="handleSubmit" class="space-y-6">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label for="name" class="block text-sm font-medium text-ink mb-2">
                  Full Name
                </label>
                <input
                  id="name"
                  v-model="form.name"
                  type="text"
                  required
                  placeholder="John Smith"
                  class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                />
              </div>
              
              <div>
                <label for="email" class="block text-sm font-medium text-ink mb-2">
                  Work Email
                </label>
                <input
                  id="email"
                  v-model="form.email"
                  type="email"
                  required
                  placeholder="john@company.com"
                  class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label for="company" class="block text-sm font-medium text-ink mb-2">
                Company Name
              </label>
              <input
                id="company"
                v-model="form.company"
                type="text"
                required
                placeholder="ACME Corporation"
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
              />
            </div>

            <div>
              <label for="marketplaces" class="block text-sm font-medium text-ink mb-2">
                Current Marketplaces
              </label>
              <select
                id="marketplaces"
                v-model="form.marketplaces"
                multiple
                placeholder="Select the marketplaces you sell on. Hold Ctrl or Cmd to select multiple."
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
              >
                <option value="amazon">Amazon</option>
                <option value="shopify">Shopify</option>
                <option value="walmart">Walmart</option>
                <option value="tiktok">TikTok Shop</option>
                <option value="target">Target</option>
              </select>
              <p class="text-xs text-muted mt-1">Hold Ctrl on Windows or Cmd on Mac to select multiple marketplaces</p>
            </div>

            <div>
              <label for="interests" class="block text-sm font-medium text-ink mb-2">
                What Interests You Most?
              </label>
              <textarea
                id="interests"
                v-model="form.interests"
                rows="4"
                placeholder="Tell us about your biggest challenges and what you want to see demoed. For example: We need better inventory management across Amazon and Shopify or We want to improve our product listing process"
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent resize-none"
              ></textarea>
              <p class="text-xs text-muted mt-1">The more details you provide, the more personalized we can make your demo</p>
            </div>

            <!-- Error Message -->
            <div v-if="submitError" class="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {{ submitError }}
            </div>

            <!-- Success Message -->
            <div v-if="submitSuccess" class="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
              Thank you! We have received your demo request. Our team will contact you within 24 hours to schedule your personalized demo.
            </div>

            <BaseButton
              type="submit"
              variant="primary"
              size="lg"
              class="w-full"
              :disabled="isSubmitting"
            >
              {{ isSubmitting ? 'Submitting...' : 'Schedule My Demo' }}
            </BaseButton>
          </form>
        </BaseCard>
      </div>
    </Section>

    <!-- FAQ Section -->
    <Section class="py-20">
      <div class="max-w-3xl mx-auto">
        <h2 class="text-3xl font-bold text-ink text-center mb-12">
          Frequently Asked Questions
        </h2>
        <div class="space-y-6">
          <div class="border border-gray-200 rounded-lg p-6">
            <h3 class="text-lg font-semibold text-ink mb-2">How long does a demo take?</h3>
            <p class="text-muted">Our demos typically last 30-45 minutes, depending on your questions and the depth of exploration you would like. We will adapt based on your needs and can always schedule follow-up sessions if needed.</p>
          </div>
          <div class="border border-gray-200 rounded-lg p-6">
            <h3 class="text-lg font-semibold text-ink mb-2">Is there a cost for the demo?</h3>
            <p class="text-muted">No! Our demos are completely free. There is no obligation to purchase, and we are happy to answer all your questions about whether E2M is the right fit for your business.</p>
          </div>
          <div class="border border-gray-200 rounded-lg p-6">
            <h3 class="text-lg font-semibold text-ink mb-2">Do I need to prepare anything?</h3>
            <p class="text-muted">Not at all! Just come ready to discuss your current challenges and what you would like to accomplish. If you want, you can have your current marketplace metrics handy so we can show you potential improvements.</p>
          </div>
          <div class="border border-gray-200 rounded-lg p-6">
            <h3 class="text-lg font-semibold text-ink mb-2">What happens after the demo?</h3>
            <p class="text-muted">After your demo, our team will follow up with a personalized proposal, answer any remaining questions, and discuss next steps if you are interested. We are here to support your decision-making process.</p>
          </div>
        </div>
      </div>
    </Section>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import Section from '@/components/Section.vue'
import BaseButton from '@/components/BaseButton.vue'
import BaseCard from '@/components/BaseCard.vue'
import { loadRecaptcha, submitDemoForm } from '@/utils/formSubmission'

// Initialize reCAPTCHA on component mount
onMounted(() => {
  loadRecaptcha().catch(error => console.warn('reCAPTCHA failed to load:', error))
})

const form = ref({
  name: '',
  email: '',
  company: '',
  marketplaces: [],
  interests: ''
})

const isSubmitting = ref(false)
const submitSuccess = ref(false)
const submitError = ref('')

const handleSubmit = async () => {
  isSubmitting.value = true
  submitError.value = ''
  submitSuccess.value = false

  try {
    const marketplacesText = form.value.marketplaces.join(', ')
    const result = await submitDemoForm({
      name: form.value.name,
      email: form.value.email,
      company: form.value.company,
      marketplaces: marketplacesText,
      message: form.value.interests
    })

    if (result.success) {
      submitSuccess.value = true
      // Reset form
      form.value = {
        name: '',
        email: '',
        company: '',
        marketplaces: [],
        interests: ''
      }
      // Hide success message after 5 seconds
      setTimeout(() => {
        submitSuccess.value = false
      }, 5000)
    } else {
      submitError.value = result.error || 'Failed to submit form'
    }
  } catch (error) {
    submitError.value = error instanceof Error ? error.message : 'An error occurred'
  } finally {
    isSubmitting.value = false
  }
}
</script>
